// Teacher OS API client (spec R12): same-origin cookie, CSRF header,
// If-Match on revisioned writes, 401 sends you to /login.

import { signal } from '@preact/signals';

export interface Teacher { id: string; email: string; name: string; display_title: string; must_change_password?: boolean; admin?: boolean }

export interface ApiFail { status: number; error: string; fields?: Record<string, string>; current?: unknown }

export class ApiErr extends Error implements ApiFail {
  constructor(public status: number, public error: string, public fields?: Record<string, string>, public current?: unknown) { super(error); }
}

export async function api<T>(method: string, path: string, body?: unknown, ifMatch?: number): Promise<T> {
  const headers: Record<string, string> = { 'X-Requested-With': 'tos' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (ifMatch !== undefined) headers['If-Match'] = String(ifMatch);
  const res = await fetch(path, { method, credentials: 'same-origin', headers, body: body === undefined ? undefined : JSON.stringify(body) });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    me.value = null;
    const back = location.pathname + location.search;
    if (!location.pathname.startsWith('/login')) location.assign(`/login?next=${encodeURIComponent(back)}`);
  }
  // §03: a reset password must be changed first; show the change screen.
  if (res.status === 403 && json.error === 'change password' && me.value) me.value = { ...me.value, must_change_password: true };
  if (!res.ok) throw new ApiErr(res.status, json.error ?? `HTTP ${res.status}`, json.fields, json.current);
  return json as T;
}

/** undefined = not checked yet, null = logged out. */
export const me = signal<Teacher | null | undefined>(undefined);

export async function loadMe(): Promise<void> {
  try { me.value = (await api<{ teacher: Teacher | null }>('GET', '/api/auth/me')).teacher; } catch { me.value = null; }
}

// ── Shapes returned by the server ─────────────────────────────────────

export interface ClassRow { id: string; academic_year: number; name: string; year_level: number; archived: number; student_count: number; revision: number; rolled_from_id?: string | null }
export interface StudentRow { id: string; class_id: string; name: string; student_code: string | null; archived: number; revision: number }
export interface AssessmentRow {
  id: string; title: string; year_level: number; criterion: string; sat_on: string | null; stage: 'setup' | 'marking' | 'analysed';
  extension_item_id: string | null; marking_started_at: string | null; revision: number; class_ids?: string[]; class_dates?: Record<string, string | null>;
  item_count?: number; student_count?: number;
  status?: 'active' | 'finalised'; finalised_at?: string | null; finalised_by?: string | null; created_at?: string;
}
export interface ItemRow { id: string; label: string; position: number; prompt: string; max_mark: number; threshold: number; topic: string; strand: string; command_term: string }
export interface CodeRow { id: string; code: string; meaning: string; short_label: string; item_ids: string[] }
export interface EnrolmentRow {
  student_id: string; name: string; student_code: string | null; class_id_at_sitting: string; class_name_at_sitting: string;
  attendance: 'present' | 'absent' | 'catchup' | 'exempt'; identity_status: 'unverified' | 'verified'; revision: number; evidence_revision: number;
}
export interface ResultRow { student_id: string; item_id: string; mark: number | null; unavailable_reason: string | null; blank: boolean; evidence: string | null; codes: string[]; revision: number }

export interface Dataset {
  assessment: AssessmentRow;
  items: ItemRow[];
  misconceptions: CodeRow[];
  students: EnrolmentRow[];
  results: ResultRow[];
  judgements: { student_id: string; teacher_level: number | null; teacher_note: string | null; status: string; disposition?: string; disposition_reason?: string | null }[];
  feedback: { student_id: string; strength: string | null; improvement: string | null; next_step: string | null; target: string | null; status: string; not_applicable_reason?: string | null }[];
  classes: string[];
  progression?: { item_id: string; step: number }[];
  scan_exceptions?: { student_id: string; reason: string; note: string | null }[];
  checkpoints?: { n: number; passed_by: string; passed_at: string }[];
  analysis_notes?: { tab: 'item' | 'strand' | 'topic' | 'command'; text: string; revision: number; basis_digest: string; updated_by: string; updated_at: string; stale: string | null }[];
}
